<template>
  <div class="pdt-20px">
    <div class="card">
      <header class="header-card">
        <p>
          {{ $t('ajoutDevis.generalTitle.generalInformations')}}
        </p>
      </header>
      <div class="card-content">
        <div class="columns is-multiline">
          <div class="column is-2">
            <b-field :label="$t('ajoutDevis.informations.quotationDate')">
              <b-datepicker show-week-number
                            nearby-selectable-month-days
                            v-model="devis.dateDevisCommande"
                            icon="calendar-today"
                            :editable="true">
              </b-datepicker>
            </b-field>
          </div>
          <div class="column is-1">
            <b-field class="bfieldWhitoutLabel">
              <b-checkbox
                v-model="devis.estSigne">
                {{ $t('ajoutDevis.informations.estSigne') }}
              </b-checkbox>
            </b-field>
          </div>
          <div class="column is-3">
            <b-field :label="$t('ajoutDevis.informations.validityDate')">
              <b-datepicker show-week-number
                            nearby-selectable-month-days
                            v-model="devis.dateValidite"
                            icon="calendar-today"
                            :editable="true">
              </b-datepicker>
            </b-field>
          </div>
          <div class="column is-3">
            <b-field :label="$t('ajoutDevis.informations.datePremierAffichage')">
              <b-datepicker show-week-number
                            :editable="true"
                            nearby-selectable-month-days
                            v-model="devis.datePremierAffichage"
                            icon="calendar-today"
                            :disabled="isDateCampagneDisabled">
              </b-datepicker>
            </b-field>
          </div>
          <div class="column is-3">
            <b-field :label="$t('ajoutDevis.informations.categorieVente')">
              <b-select expanded v-model="devis.categorieVente">
                <option :key="categorieVente.code"
                        :value="categorieVente.code"
                        v-for="categorieVente in categorieProduit">
                  {{ $t(categorieVente.libelle) }}
                </option>
              </b-select>
            </b-field>
          </div>
          <div class="column is-3">
            <user-selector
              v-model="devis.commercialId"
              @input="setCommissions"
              :critere="(u) => [constantes.COMMERCIAL, constantes.DIRECTEUR_COMMERCIAL]
                .includes(u.profil)"
              :placeholder="$t('ajoutDevis.informations.aucunCommercial')"
              :label="$t('ajoutDevis.informations.trade')"
              :disabled="isCommercial"
            />
          </div>
          <div class="column is-3">
            <user-selector
              v-model="devis.assistantId"
              :critere="(u) => [constantes.ASSISTANT_COMMERCIAL]
                .includes(u.profil)"
              :label="$t('ajoutDevis.informations.assistant')"
              :placeholder="$t('ajoutDevis.informations.aucunAssistant')"
            />
          </div>
          <div class="column is-3" v-if="agencesSelectionnees.length > 1">
            <selection-agence v-model="devis.agenceId"
                              obligatoire>
            </selection-agence>
          </div>
          <div class="column is-3" v-if="societeActive.roles.length > 1">
            <b-field :label="$t('ajoutDevis.informations.role')" class="obligatoire">
              <b-select expanded v-model="devis.role">
                <option :key="role.code" :value="role.code"
                        v-for="role in getRoleOfCurrentSociete">
                  {{ $t(role.libelle) }}
                </option>
              </b-select>
            </b-field>
          </div>
          <div class="column is-3">
            <b-field :label="$t('ajoutDevis.informations.customerReference')">
              <b-input v-model="devis.referenceClient">
              </b-input>
            </b-field>
          </div>
          <div class="column is-3">
            <b-field :label="$t('ajoutDevis.informations.folderCode')">
              <b-input v-model="devis.codeDossier">
              </b-input>
            </b-field>
          </div>
          <div class="column"
            v-if="moduleCampagneActive"
            :class="afficherAgenceEtSociete ? 'is-6' : 'is-3'">
            <selection-campagne
              :label="$t('ajoutDevis.informations.campagne')"
              :dcf="devis"></selection-campagne>
          </div>
          <div class="column is-6">
            <b-field :label="$t('ajoutDevis.informations.theme')">
              <b-input v-model="devis.theme"></b-input>
            </b-field>
          </div>
          <div class="column is-3">
            <b-field :label="$t('editionCommande.informations.tags')">
              <query-tags-tag-input
                :tooltipLabel="$t('editionCommande.informations.tagInfo')"
                position="is-top"
                v-model="devis.tags"
                field="tag"
                :moduleTag="constantes.DOCUMENT_VENTE">
              </query-tags-tag-input>
            </b-field>
          </div>
          <div class="column is-3" v-if="modulePresseActive">
            <b-field :label="$t('editionCommande.informations.planMedia')">
              <b-autocomplete
                :value="searchPlanMedia"
                :data="planMediaFilter"
                :loading="isFetchingPlanMedia"
                :disabled="!!$route.params.planMediaId"
                @select="setPlanMedia"
                @input="getAsyncPlanMediaData"
                field="code"
                icon="magnify"
                type="is-primary"
                open-on-focus>
                <template slot="empty">
                  <div v-if="!isFetchingPlanMedia">
                    {{$t('general.autocomplete.noRslt')}}
                  </div>
                  <div v-else>
                    {{$t('general.autocomplete.search')}}
                  </div>
                </template>
              </b-autocomplete>
            </b-field>
          </div>
          <div class="column is-3" v-if="modulePresseActive">
            <b-field
                :label="$t('editionCommande.informations.regleFacturation.label')"
                :message="!isSyncRegleWithLignes ?
                          $t('editionCommande.informations.regleFacturation.erreur') : ''"
                :type="!isSyncRegleWithLignes ? 'is-warning' : ''">
              <b-select expanded v-model="devis.regleFacturationId">
                <option :value="null"></option>
                <option
                    v-for="regle in reglesFacturation"
                    :key="regle.id"
                    :value="regle.id">
                  {{ regle.libelle }}
                </option>
              </b-select>
            </b-field>
          </div>
        </div>
      </div>
    </div>
    <br>
    <div class="card">
      <header class="header-card">
        <p>
          {{ $t('ajoutDevis.generalTitle.clientsSelect')}}
        </p>
      </header>
      <div class="card-content">
        <app-selection-client :nouveauDevisCommande="devis"
                              :gestion-echeancier="gestionEcheancier"
                              @calcul="$emit('calcul')"
                              @regenereEcheancier="$emit('regenereEcheancier')"
                              @deleteOrDeselectPayeurInEcheancier="
                              $emit('deleteOrDeselectPayeurInEcheancier', $event)"
        />
      </div>
    </div>
    <br>
    <div class="card">
      <header class="header-card">
        <p>
          {{ $t('ajoutDevis.generalTitle.articles')}}
        </p>
      </header>
      <div class="card-content">
        <liste-lignes-ventes
          :dcf="devis"
          :error="error"
          :breadcrumb="breadcrumb"
          @typeProduitChange="findNewTypeProduit()"
          @calcul="$emit('calcul')">
        </liste-lignes-ventes>
      </div>
      <app-conditions-reglement :devisCommande="devis"
                                racineTraduction="ajoutDevis"/>
    </div>
    <br>
    <commentaires
      :type="devis.type"
      :devisCommandeFacture="devis">
    </commentaires>
    <br />
    <div class="card">
      <header class="header-card">
        <p>
          {{ $t('piecesJointes.attachments')}}
        </p>
      </header>
      <div class="card-content">
        <app-pieces-jointes :checkPermissions="true"/>
      </div>
    </div>
  </div>
</template>

<script>

import { createNamespacedHelpers } from 'vuex';
import debounce from 'lodash/debounce';
import { getAll as getAllPlanMedia } from '@/api/planMedia';
import serverTableDataTransformer from '@/services/planMedia/serverTableDataTransformer';
import commentaires from '@/components/bodac/dcf/addEditDcf/commentaires/commentaires';
import listeLignesVentes from '@/components/devisCommandes/editionDevisCommande/listeLignesVentes';
import SelectionClient from '@/components/devisCommandes/editionDevisCommande/selectionClients';
import ConditionsReglement from '@/components/devisCommandes/editionDevisCommande/conditionsReglement';
import PiecesJointes from '@/components/general/piecesJointes';
import categorieVenteInitialize from '@/services/devisCommandes/categorieVenteInitialize';
import {
  CATEGORIE_DIVERS,
  CATEGORIE_SALON,
  CATEGORIE_SPONSORING,
  CATEGORIE_AUTRE,
} from '@/configuration/catalogue/produitConf';
import { COMMERCIAL, DIRECTEUR_COMMERCIAL, ASSISTANT_COMMERCIAL } from '@/configuration/parametrage/utilisateurConf';
import { SOCIETE_ROLE_VENTE } from '@/configuration/societe/societe';
import { RESULTS_PER_PAGE } from '@/configuration/clientTableConf';
import * as typesParametres from '@/store/modules/parametres-mutation-type';
import messageErreur from '@/services/general/messageErreur';
import selectionAgence from '@/components/general/selectionAgence';
import userSelector from '@/components/commun/user/userSelector';
import selectionCampagne from '@/components/devisCommandes/selectionCampagne';
import queryTagsTagInput from '@/components/general/tagInput/queryTagsTagInput';
import { DOCUMENT_VENTE } from '@/configuration/parametrage/tagConf';

const Societe = createNamespacedHelpers('societe');
const Parametres = createNamespacedHelpers('parametres');
const CategorieVente = createNamespacedHelpers('categorieVente');

export default {
  data() {
    return {
      constantes: {
        CATEGORIE_DIVERS,
        CATEGORIE_SALON,
        CATEGORIE_SPONSORING,
        CATEGORIE_AUTRE,
        COMMERCIAL,
        DIRECTEUR_COMMERCIAL,
        ASSISTANT_COMMERCIAL,
        DOCUMENT_VENTE,
      },
      searchPlanMedia: '',
      planMediaFilter: [],
      isFetchingPlanMedia: false,
    };
  },
  props: {
    devis: {
      type: Object,
      required: true,
    },
    breadcrumb: {
      type: Array,
      required: true,
    },
    isNewDevis: {
      type: Boolean,
      required: true,
    },
    error: {
      type: Boolean,
      required: true,
    },
    utilisateur: {
      type: Object,
      required: true,
    },
    gestionEcheancier: {
      type: Boolean,
      required: true,
    },
  },
  components: {
    queryTagsTagInput,
    commentaires,
    appConditionsReglement: ConditionsReglement,
    appPiecesJointes: PiecesJointes,
    listeLignesVentes,
    appSelectionClient: SelectionClient,
    selectionAgence,
    userSelector,
    selectionCampagne,
  },
  computed: {
    ...Societe.mapGetters({
      societeActiveId: 'societeActiveId',
      societeActive: 'societeActive',
      agencesSelectionnees: 'agenceActive',
    }),
    ...Parametres.mapGetters({
      parametresInterne: 'parametresInterne',
      parametre: 'parametre',
    }),
    ...CategorieVente.mapGetters({
      categorieProduit: 'categorieProduit',
    }),
    isDateCampagneDisabled() {
      const typesDateCampagneEnabled = [
        this.constantes.CATEGORIE_DIVERS,
        this.constantes.CATEGORIE_SALON,
        this.constantes.CATEGORIE_SPONSORING,
        this.constantes.CATEGORIE_AUTRE,
      ];
      return !typesDateCampagneEnabled.includes(this.devis.categorieVente);
    },
    isCommercial() {
      return ((this.isNewDevis && this.utilisateur.profil === COMMERCIAL)
        || (!this.isNewDevis && this.utilisateur.profil === COMMERCIAL
          && this.utilisateur.id === this.devis.commercialId));
    },
    reglesFacturation() {
      return this.parametre(typesParametres.PARAMETRES_KEYS.regleFacturation)
        .filter(r => r.societeId === (this.devis?.societeId || this.societeActiveId));
    },
    getRoleOfCurrentSociete() {
      return SOCIETE_ROLE_VENTE.filter((role) => {
        return this.societeActive.roles.includes(role.code);
      });
    },
    modulePresseActive() {
      return this.parametresInterne(typesParametres.PARAMETRES_INTERNE_KEYS.modulePresse.key);
    },
    moduleCampagneActive() {
      return this.parametresInterne(typesParametres.PARAMETRES_INTERNE_KEYS.campagne.key);
    },
    isSyncRegleWithLignes() {
      if (this.devis.regleFacturationId) {
        return this.devis.lignesCommande.length > 0
          && this.devis.lignesCommande.some(ligne => {
            return ligne.produit
              && (ligne.produit.regleFacturationId === this.devis.regleFacturationId);
          });
      }
      return true;
    },
    afficherAgenceEtSociete() {
      return this.agencesSelectionnees.length > 1 && this.societeActive.roles.length > 1;
    },
  },
  methods: {
    ...Parametres.mapActions({
      loadParametresInterne: 'loadParametresInterne',
      loadParametres: 'loadParametres',
    }),
    findNewTypeProduit() {
      categorieVenteInitialize(this.devis);
    },
    getAsyncPlanMediaData: debounce(async function getData(text) {
      if (text === this.searchPlanMedia) return;
      try {
        this.isFetchingPlanMedia = true;
        const payload = {
          skipCount: 0,
          maxResultCount: RESULTS_PER_PAGE,
          query: {
            auto: text,
            societeId: this.societeActiveId,
          },
        };
        serverTableDataTransformer(payload);
        this.planMediaFilter = (await getAllPlanMedia(payload))
          .data.result.items;
        this.isFetchingPlanMedia = false;
      } catch (e) {
        messageErreur(this.$i18n);
        this.isFetchingPlanMedia = false;
        throw e;
      }
    }),
    setPlanMedia(planMedia) {
      if (!planMedia) return;
      this.searchPlanMedia = planMedia.code;
      this.devis.planMediaId = planMedia.id;
    },
    setCommissions(commercialId) {
      if (!this.modulePresseActive || this.devis.commissions.length > 1) return;
      if (!commercialId) this.devis.commissions = [];
      else {
        this.devis.commissions = [{
          commercialId: commercialId,
          pourcentageRepartition: 100,
          selected: false,
        }];
      }
    },
  },
  async mounted() {
    try {
      await this.loadParametresInterne([
        typesParametres.PARAMETRES_INTERNE_KEYS.modulePresse.key,
        typesParametres.PARAMETRES_INTERNE_KEYS.campagne.key]);
      await this.loadParametres([typesParametres.PARAMETRES_KEYS.regleFacturation]);
      const planMediaId = this.$route.params.planMediaId || this.devis.planMediaId;
      if (planMediaId && this.devis.planMedia) {
        this.searchPlanMedia = this.devis.planMedia.code;
      }
    } catch (e) {
      messageErreur(this.$i18n);
      throw (e);
    }
  },
};
</script>
